'use client';

/* eslint-disable @next/next/no-img-element */
import AccessiblePrice from '@/components/AccessiblePrice';
import { useJump } from '@/components/MagicJump';
import StarRating from '@/components/StarRating';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import { useId, type MouseEvent } from 'react';
import {
  getCompactRankingProductImages,
  getBestsellerImageCandidates,
  getTopRankingProductImages,
  shouldCoverBestsellerImage,
} from './bestsellerImages';
import { toProduct } from './productAdapter';
import type { BestsellerProduct } from './types';
import BestsellerFallbackImage from './BestsellerFallbackImage';
import BestsellerTagIcon from './BestsellerTagIcon';
import {
  BESTSELLER_RANKING_CARD_LAYOUT_CLASS_NAMES,
  BESTSELLER_RANKING_HOVER_MEDIA_CLASS_NAME,
  BESTSELLER_TAG_CLASS_NAMES,
} from './bestsellerStyles';
import type { BestsellerProductTrackingHandlers } from './bestsellerTracking';
import BestsellerTrackedWish from './BestsellerTrackedWish';
import styles from './RankingCard.module.css';
import { isBestsellerProductUnavailable } from './bestsellerAvailability';
import BestsellerSoldOutOverlay from './BestsellerSoldOutOverlay';
import { useBestsellerDisplayPrice } from './bestsellerPrice';

function RankBadge({ rank, compact = false }: { rank: number; compact?: boolean }) {
  return (
    <div
      className={`absolute top-0 z-[2] flex flex-col items-center bg-[#463ae8] text-white ${
        compact
          ? 'left-[12px] h-[36px] w-[30px] text-[12px] xl:left-[16px] 2xl:left-[16px]'
          : 'left-[16px] h-[56px] w-[48px] text-[16px]'
      }`}
      style={{ clipPath: 'polygon(0 0,100% 0,100% 100%,50% 82%,0 100%)' }}
    >
      <span className={`font-bb font-bold leading-none ${compact ? 'mt-[2px]' : 'mt-[4px]'}`}>
        TOP
      </span>
      <span className={`font-bb font-bold leading-none ${compact ? 'mt-0' : 'mt-[5px]'}`}>
        {rank}
      </span>
    </div>
  );
}

function Rating({ rating, compact = false }: { rating: string; compact?: boolean }) {
  const ratingValue = Number(rating) || 0;
  const halfStepRating = Math.floor(ratingValue * 2) / 2;

  return (
    <div className={`flex items-center ${styles.rating}`}>
      <StarRating
        value={halfStepRating}
        readOnly
        precision={0.5}
        iconSize="1em"
        gap="0.15em"
        className={styles.ratingStars}
        sx={{
          '& .MuiRating-iconEmpty': { color: '#eeeeee' },
        }}
      />
      <span
        className={`font-br text-[#666666] ${styles.ratingText} ${
          compact ? styles.compactRatingText : ''
        }`}
      >
        {rating}
      </span>
    </div>
  );
}

function PurchasedPill({
  text,
  type,
  large = false,
}: {
  text: string;
  type?: BestsellerProduct['tagType'];
  large?: boolean;
}) {
  if (!text || !type) return null;
  const isTrend = type === 'trend';

  return (
    <span
      className={`inline-flex max-w-full items-center rounded-[3px] font-bb 2xl:rounded-[6px] ${styles.purchasedPill} ${
        isTrend ? BESTSELLER_TAG_CLASS_NAMES.trend : BESTSELLER_TAG_CLASS_NAMES.sales
      }`}
    >
      <BestsellerTagIcon
        type={isTrend ? 'trend' : 'sales'}
        large={large}
        className={`${styles.pillIcon} ${large ? 'mr-[3px]' : 'mr-[2px]'}`}
      />
      <span className="truncate">{text}</span>
    </span>
  );
}

function ProductWish({
  product,
  large = false,
  onCollect,
  onWishChange,
}: {
  product: BestsellerProduct;
  large?: boolean;
  onCollect: BestsellerProductTrackingHandlers['onCollect'];
  onWishChange?: (isWish: number) => void;
}) {
  const wishProduct = toProduct(product);

  return (
    <div className="pointer-events-none absolute inset-0 z-[4]">
      <BestsellerTrackedWish
        data={wishProduct}
        className={`pointer-events-auto absolute leading-none ${
          large
            ? 'right-[20px] top-[20px] xl:right-[24px] xl:top-[24px] 2xl:right-[36px] 2xl:top-[36px]'
            : 'right-[10px] top-[11px] xl:right-[9px] xl:top-[12px] 2xl:right-[16px] 2xl:top-[16px]'
        } ${
          large
            ? '[&_button]:!h-[19px] [&_button]:!w-[19px] [&_button>div]:!h-[19px] [&_button>div]:!w-[19px] xl:[&_button]:!h-[24px] xl:[&_button]:!w-[24px] xl:[&_button>div]:!h-[24px] xl:[&_button>div]:!w-[24px] 2xl:[&_button]:!h-[32px] 2xl:[&_button]:!w-[32px] 2xl:[&_button>div]:!h-[32px] 2xl:[&_button>div]:!w-[32px]'
            : '[&_button]:!h-[16px] [&_button]:!w-[16px] [&_button>div]:!h-[16px] [&_button>div]:!w-[16px] xl:[&_button]:!h-[20px] xl:[&_button]:!w-[20px] xl:[&_button>div]:!h-[20px] xl:[&_button>div]:!w-[20px] 2xl:[&_button]:!h-[32px] 2xl:[&_button]:!w-[32px] 2xl:[&_button>div]:!h-[32px] 2xl:[&_button>div]:!w-[32px]'
        }`}
        iconClassName={
          large
            ? 'text-[19px] xl:text-[24px] 2xl:text-[32px]'
            : 'text-[16px] xl:text-[20px] 2xl:text-[32px]'
        }
        iconSize="1em"
        collectScene="bestsellers榜单"
        onCollect={onCollect}
        onWishChange={onWishChange}
      />
    </div>
  );
}

function ProductInfo({ product, large = false }: { product: BestsellerProduct; large?: boolean }) {
  const displayPrice = useBestsellerDisplayPrice(product, 'pc');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');

  return (
    <div className={`relative flex h-full min-w-0 flex-col bg-white ${styles.productInfo}`}>
      <div className="mt-auto min-w-0">
        <h3 className={`truncate font-bb font-bold text-[#282828] ${styles.productTitle}`}>
          {product.name}
        </h3>
        <Rating rating={product.rating} compact={!large} />
      </div>

      <div className={`min-w-0 ${styles.priceBlock}`}>
        <div
          className={`flex min-w-0 flex-wrap items-baseline font-bb font-bold text-[#282828] ${styles.price}`}
        >
          <AccessiblePrice
            value={displayPrice.price}
            label={tPriceA11y('srCurrent')}
            className="notranslate shrink-0"
          />
          {displayPrice.originPrice ? (
            <AccessiblePrice
              as="del"
              value={displayPrice.originPrice}
              label={tPriceA11y('srOriginal')}
              className="notranslate min-w-0 font-br font-normal text-[#666666]"
            />
          ) : null}
        </div>
        <div className={styles.pillRow}>
          <PurchasedPill text={product.purchased} type={product.tagType} large={large} />
        </div>
      </div>
    </div>
  );
}

export default function RankingCard({
  product,
  trackingHandlers,
  exposureKey = product.shopSku,
  returnTargetKey,
  onBeforeNavigate,
  onWishChange,
  large = false,
}: {
  product: BestsellerProduct;
  trackingHandlers: BestsellerProductTrackingHandlers;
  exposureKey?: string;
  returnTargetKey?: string;
  onBeforeNavigate?: () => void;
  onWishChange?: (isWish: number) => void;
  large?: boolean;
}) {
  const toJumpTarget = useJump();
  const { productImage, hoverImage } = large
    ? getTopRankingProductImages(product)
    : getCompactRankingProductImages(product);
  const shouldShowHoverImage = Boolean(hoverImage?.src);
  const hoverImageShouldCover = hoverImage ? shouldCoverBestsellerImage(hoverImage.kind) : false;
  const unavailable = isBestsellerProductUnavailable(product);
  const soldOutDescriptionId = useId();
  const { ref: exposureRef } = useValidExposure({
    callback: trackingHandlers.onProductExposure,
    ts: exposureKey,
  });

  const jumpToProduct = () => {
    onBeforeNavigate?.();
    trackingHandlers.onProductClick();
    toJumpTarget({
      jump_type: 4,
      Jump_target: product.href,
    });
  };

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    jumpToProduct();
  };

  if (large) {
    return (
      <div
        ref={exposureRef}
        data-ranking-product-return-target={returnTargetKey}
        className={`${BESTSELLER_RANKING_CARD_LAYOUT_CLASS_NAMES.large} ${styles.largeCard} group relative cursor-pointer text-[#282828]`}
      >
        <a
          href={product.href}
          aria-label={product.name}
          aria-describedby={unavailable ? soldOutDescriptionId : undefined}
          className="peer absolute inset-0 z-[3] rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#463ae8]"
          onClick={handleLinkClick}
        />
        <div
          role="presentation"
          className="group/ranking-product-image relative flex h-[200px] items-center justify-center bg-white md:h-full"
        >
          <RankBadge rank={product.rank} />
          <a
            href={product.href}
            aria-hidden="true"
            tabIndex={-1}
            className="absolute inset-0 z-[3]"
            onClick={handleLinkClick}
          >
            <span className="sr-only">{product.name}</span>
          </a>
          <BestsellerFallbackImage
            sources={[
              productImage.src,
              ...getBestsellerImageCandidates(product, productImage.kind),
            ]}
            alt={product.name}
            className={`h-auto w-[72%] max-w-[420px] object-contain mix-blend-multiply transition-opacity duration-300 ${
              unavailable ? 'opacity-50' : ''
            } ${
              shouldShowHoverImage
                ? 'group-hover/ranking-product-image:opacity-0 peer-focus-visible:opacity-0'
                : ''
            }`}
          />
          {shouldShowHoverImage && hoverImage && (
            <div
              className={`absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover/ranking-product-image:opacity-100 peer-focus-visible:opacity-100 ${BESTSELLER_RANKING_HOVER_MEDIA_CLASS_NAME}`}
            >
              <BestsellerFallbackImage
                sources={[
                  hoverImage.src,
                  ...getBestsellerImageCandidates(product, hoverImage.kind),
                ]}
                alt=""
                className={
                  hoverImageShouldCover
                    ? `h-full w-full object-cover ${unavailable ? 'opacity-50' : ''}`
                    : `h-auto max-h-full w-[72%] max-w-[420px] object-contain mix-blend-multiply ${
                        unavailable ? 'opacity-50' : ''
                      }`
                }
              />
            </div>
          )}
          <BestsellerSoldOutOverlay
            descriptionId={soldOutDescriptionId}
            product={product}
            platform="pc"
          />
        </div>
        <div role="presentation" className="hidden h-full overflow-hidden bg-[#eeeeee] md:block">
          <BestsellerFallbackImage
            sources={[
              product.modelImage,
              ...getBestsellerImageCandidates(product, 'celebrity'),
              ...getBestsellerImageCandidates(product, 'material'),
              ...getBestsellerImageCandidates(product, 'product'),
            ]}
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
        <ProductInfo product={product} large />
        <ProductWish
          product={product}
          large
          onCollect={trackingHandlers.onCollect}
          onWishChange={onWishChange}
        />
      </div>
    );
  }

  return (
    <div
      ref={exposureRef}
      data-ranking-product-return-target={returnTargetKey}
      className={`${BESTSELLER_RANKING_CARD_LAYOUT_CLASS_NAMES.compact} ${styles.compactCard} group relative cursor-pointer text-[#282828]`}
    >
      <a
        href={product.href}
        aria-label={product.name}
        aria-describedby={unavailable ? soldOutDescriptionId : undefined}
        className="peer absolute inset-0 z-[3] rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#463ae8]"
        onClick={handleLinkClick}
      />
      <div
        role="presentation"
        className="relative flex h-full items-center justify-center bg-white"
      >
        <RankBadge rank={product.rank} compact />
        <BestsellerFallbackImage
          sources={[productImage.src, ...getBestsellerImageCandidates(product, productImage.kind)]}
          alt={product.name}
          className={`h-auto w-[72%] object-contain mix-blend-multiply transition-opacity duration-300 ${
            unavailable ? 'opacity-50' : ''
          } ${shouldShowHoverImage ? 'group-hover:opacity-0 peer-focus-visible:opacity-0' : ''}`}
        />
        {shouldShowHoverImage && hoverImage && (
          <div
            className={`absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100 peer-focus-visible:opacity-100 ${BESTSELLER_RANKING_HOVER_MEDIA_CLASS_NAME} ${
              hoverImageShouldCover ? '' : 'px-[11%] py-[30%]'
            }`}
          >
            <BestsellerFallbackImage
              sources={[hoverImage.src, ...getBestsellerImageCandidates(product, hoverImage.kind)]}
              alt=""
              className={
                hoverImageShouldCover
                  ? `h-full w-full object-cover ${unavailable ? 'opacity-50' : ''}`
                  : `h-auto max-h-full w-full object-contain mix-blend-multiply ${
                      unavailable ? 'opacity-50' : ''
                    }`
              }
            />
          </div>
        )}
        <BestsellerSoldOutOverlay
          descriptionId={soldOutDescriptionId}
          product={product}
          platform="pc"
        />
      </div>
      <ProductInfo product={product} />
      <ProductWish
        product={product}
        onCollect={trackingHandlers.onCollect}
        onWishChange={onWishChange}
      />
    </div>
  );
}
